import {
  addTeamMember,
  getTeamMembers,
  inviteTeamMember,
  removePendingTeamMember,
  removeTeamMember
} from '@/services/team';
import type { TeamMember } from '@/types/team-types';
import { defineStore } from 'pinia';

type State = {
  members: {
    loading: boolean;
    loaded: boolean;
    data: TeamMember[];
    error: string | null;
  };
  pendingInvitation: {
    loading: boolean;
    error: string | null;
  };
  addMember: {
    loading: boolean;
    error: string | null;
  };
  removeMember: {
    loading: boolean;
    error: string | null;
    memberId: number | null;
  };
};

export const useTeamStore = defineStore('team', {
  state: (): State => ({
    members: {
      loading: false,
      loaded: false,
      data: [],
      error: null
    },
    pendingInvitation: {
      loading: false,
      error: null
    },
    addMember: {
      loading: false,
      error: null
    },
    removeMember: {
      loading: false,
      error: null,
      memberId: null
    }
  }),

  getters: {
    getTeamMembers: (state) => state.members.data,
    getSeatsOccupied: (state) => state.members.data.length,
    isTeamMembersLoading: (state) => state.members.loading,
    hasTeamMembersLoaded: (state) => state.members.loaded,
    getTeamMembersError: (state) => state.members.error,

    isPendingInvitation: (state) => state.pendingInvitation.loading,
    getPendingInvitationError: (state) => state.pendingInvitation.error,

    isAddingMember: (state) => state.addMember.loading,
    getAddMemberError: (state) => state.addMember.error,

    isRemovingMember: (state) => state.removeMember.loading,
    getRemoveMemberError: (state) => state.removeMember.error,
    getRemovingMemberId: (state) => state.removeMember.memberId,

    // Active members (not pending invitations)
    getActiveMembers: (state) =>
      state.members.data.filter((member) => member.status === 'active'),

    // Pending invitations
    getPendingMembers: (state) =>
      state.members.data.filter((member) => member.status === 'pending')
  },

  actions: {
    async fetchTeamMembers() {
      try {
        this.$state.members.loading = true;
        this.$state.members.error = null;

        const { data } = await getTeamMembers();
        if (data.value) {
          this.$state.members.data = data.value.data;
          this.$state.members.loaded = true;
        } else {
          this.$state.members.error = 'Failed to fetch team members';
        }
      } catch (err: any) {
        this.$state.members.error =
          err.message || 'Error fetching team members';
      } finally {
        this.$state.members.loading = false;
      }
    },

    async addExistingMember(userId: number, role: string) {
      try {
        this.$state.addMember.loading = true;
        this.$state.addMember.error = null;

        const response = await addTeamMember(userId, role);
        const { data } = response as { data: { value?: TeamMember } };

        if (data.value) {
          // Add the new member to the list if not already present
          // Make a proper null check to fix 'possibly null or undefined' error
          if (data.value.id) {
            const exists = this.$state.members.data.some(
              (member) => member.id === data.value?.id
            );

            // Only push if it's a complete TeamMember object
            if (
              !exists &&
              'name' in data.value &&
              'email' in data.value &&
              'role' in data.value &&
              'status' in data.value &&
              'created_at' in data.value &&
              'updated_at' in data.value
            ) {
              // TypeScript will now recognize this as a TeamMember
              const memberToAdd = data.value as TeamMember;
              this.$state.members.data.push(memberToAdd);
            }
          }
          return true;
        } else {
          this.$state.addMember.error = 'Failed to add team member';
          return false;
        }
      } catch (err: any) {
        this.$state.addMember.error = err.message || 'Error adding team member';
        return false;
      } finally {
        this.$state.addMember.loading = false;
      }
    },

    async inviteNewMember(email: string, role: string, name?: string) {
      try {
        this.$state.pendingInvitation.loading = true;
        this.$state.pendingInvitation.error = null;

        // Fix the type of the response
        const response = (await inviteTeamMember(email, role, name)) as {
          data: {
            status: string;
          };
          message: string;
        };

        if (response.data.status === 'invitation_sent') {
          // We might not get a full member object back, but refresh the members list
          await this.fetchTeamMembers();
          return { success: true, message: response.message };
        } else {
          console.log('here2');
          this.$state.pendingInvitation.error = 'Failed to invite team member';
          return {
            success: false,
            message: this.$state.pendingInvitation.error
          };
        }
      } catch (err: any) {
        this.$state.pendingInvitation.error =
          err.message || 'Error inviting team member';
        return { success: false, message: this.$state.pendingInvitation.error };
      } finally {
        this.$state.pendingInvitation.loading = false;
      }
    },

    async removeMemberFromTeam(userId: number) {
      try {
        this.$state.removeMember.loading = true;
        this.$state.removeMember.error = null;
        this.$state.removeMember.memberId = userId;

        // Fix the type of the response
        const response = (await removeTeamMember(userId)) as {
          message: string;
        };

        if (response.message) {
          // Remove the member from the list
          this.$state.members.data = this.$state.members.data.filter(
            (member) => member.user_id !== userId
          );
          return { success: true, message: response.message };
        } else {
          this.$state.removeMember.error =
            response?.message || 'Failed to remove team member';
          return { success: false, message: this.$state.removeMember.error };
        }
      } catch (err: any) {
        this.$state.removeMember.error =
          err.message || 'Error removing team member';
        return { success: false, message: this.$state.removeMember.error };
      } finally {
        this.$state.removeMember.loading = false;
        this.$state.removeMember.memberId = null;
      }
    },

    async removePendingMemberFromTeam(userId: number) {
      try {
        this.$state.removeMember.loading = true;
        this.$state.removeMember.error = null;
        this.$state.removeMember.memberId = userId;

        // Fix the type of the response
        const response = (await removePendingTeamMember(userId)) as {
          message: string;
        };

        if (response.message) {
          // Remove the member from the list
          this.$state.members.data = this.$state.members.data.filter(
            (member) => member.id !== userId
          );
          return { success: true, message: response.message };
        } else {
          this.$state.removeMember.error =
            response?.message || 'Failed to remove team member';
          return { success: false, message: this.$state.removeMember.error };
        }
      } catch (err: any) {
        this.$state.removeMember.error =
          err.message || 'Error removing team member';
        return { success: false, message: this.$state.removeMember.error };
      } finally {
        this.$state.removeMember.loading = false;
        this.$state.removeMember.memberId = null;
      }
    },

    resetTeamMembers() {
      this.$state.members.loaded = false;
      this.$state.members.data = [];
      this.$state.members.error = null;
    },

    reset() {
      this.resetTeamMembers();
      this.$state.pendingInvitation.error = null;
      this.$state.addMember.error = null;
      this.$state.removeMember.error = null;
      this.$state.removeMember.memberId = null;
    }
  }
});
